import { useRouter } from 'next/router';

import {
  AdornedInput,
  Alert,
  Anchor,
  Box,
  FormField,
  FormHelp,
  Heading,
  Label,
  Paragraph,
  Popover,
  Text,
  TextButton,
  TriggerText,
} from '@zillow/constellation-10';

import {
  useRentalManagerPropertyAddressRentZestimateWebApiHandler_rentZestimate,
  useRentalManagerUserPropertyFloorPlanEditWebApiHandler_price,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useScreenSize } from '../../../hooks';
import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import accessUtils from '../../../utils/accessUtils';
import numberUtils from '../../../utils/numberUtils';
import propertyUtils from '../../../utils/propertyUtils';

import { emitLearnMoreEvent, EVENT_IDS } from '../../../analytics/events/singleFamilyTotalMonthlyPrice';
import { useAuth } from '../../AuthProvider';

const inputName = 'price';
const rentZestimateLink = 'https://www.zillow.com/z/rental/what-is-a-rent-zestimate/';

const MonthlyRentFormField = ({ isRelistingFieldset = false }: { isRelistingFieldset?: boolean }) => {
  const router = useRouter();
  const { user } = useAuth();
  const { isScreenDesktop } = useScreenSize();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const formattedCompleteAddress = propertyUtils.formatAddress({
    street: listingDetails?.street,
    city: listingDetails?.city,
    state: listingDetails?.state,
    zip: listingDetails?.zip,
  }).formattedCompleteAddress;

  const { data: rentZestimateData } = useRentalManagerPropertyAddressRentZestimateWebApiHandler_rentZestimate(
    { fullAddress: formattedCompleteAddress, unit: listingDetails?.unit },
    { enabled: !!formattedCompleteAddress },
  );

  const floorPlans = listingDetails?.floorPlans || [];
  const floorPlanId = floorPlans[0]?.id;

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    isEligible: listingDetails?.totalMonthlyPriceWithFeeNotice?.isEligible ?? false,
  });

  // Use formatPriceFromApi to resolve single price from lowPrice/highPrice
  const initialPrice = numberUtils.formatPriceFromApi(
    floorPlans[0]?.lowPrice?.toString(),
    floorPlans[0]?.highPrice?.toString(),
  );

  const updateMutation = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanEditWebApiHandler_price,
  });

  const priceField = useFieldSave({
    fieldName: inputName,
    initialValue: initialPrice,
    mutation: updateMutation,
    buildMutationVariables: (value) => ({
      getParams: { floorPlanId: floorPlanId!, price: String(value) },
    }),
  });

  const renderZestimate = () => {
    const rentZestimateCurrency = numberUtils.asCurrency(Number(rentZestimateData?.rentZestimate ?? 0));
    return (
      <Popover
        body={
          <Text>
            The Rent Zestimate® is Zillow&apos;s best estimate of a home&apos;s value as a rental property.{' '}
            <TextButton asChild>
              <a
                href={rentZestimateLink}
                rel="noopener noreferrer"
                target="_blank"
                aria-label="Learn more about the Rent Zestimate®"
              >
                Learn more
              </a>
            </TextButton>
            {' about the Rent Zestimate.'}
          </Text>
        }
        // eslint-disable-next-line constellation/sentence-case
        header={<Heading level={2}>Rent Zestimate®</Heading>}
        trigger={<TriggerText css={{ fontWeight: 'regular' }}>Rent Zestimate®: {rentZestimateCurrency}</TriggerText>}
      />
    );
  };

  const label = canAccessTotalMonthlyPriceWithFee ? 'Base rent' : 'Monthly rent';

  const priceFieldErrorDescription =
    priceField.error && priceField.error.includes('Price')
      ? priceField.error.replaceAll('Price', label)
      : priceField.error;

  return (
    <>
      {!!rentZestimateData?.rentZestimate && !isRelistingFieldset && (
        <Paragraph
          css={{ marginBottom: '600' }}
        >{`The current Rent Zestimate for this property is: ${numberUtils.asCurrency(rentZestimateData?.rentZestimate ?? 0)}`}</Paragraph>
      )}
      <FormField
        data-testid="MonthlyRentFormField"
        required={true}
        label={
          <Box
            css={{
              display: 'flex',
              alignItems: 'center',
              gap: '100',
              marginBlockEnd: 'FormField.gap.field-toLabelArea',
            }}
          >
            <Label>{label}</Label>
            {isRelistingFieldset && !!rentZestimateData?.rentZestimate && renderZestimate()}
          </Box>
        }
        control={
          <AdornedInput
            size={isScreenDesktop ? 'lg' : 'md'}
            input={
              <AdornedInput.Input
                name={inputName}
                value={String(priceField.value || '')}
                onChange={(e) => priceField.onChange(e.target.value)}
                onBlur={priceField.onBlur}
              />
            }
            startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
            endAdornment={<AdornedInput.Adornment>/month</AdornedInput.Adornment>}
          />
        }
        error={!!priceField.error}
        description={<FormHelp>{priceFieldErrorDescription}</FormHelp>}
      />

      {canAccessTotalMonthlyPriceWithFee && (
        <Alert css={{ marginBottom: '400' }}>
          You&apos;ll have the opportunity later to itemize any fees for this listing.{' '}
          <Anchor
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.zillow.com/rentals-network/pricing-transparency/"
            aria-label="Learn more about fee transparency regulations"
            onClick={() => {
              if (!user || !listingDetails) return;
              emitLearnMoreEvent({
                user,
                currentProperty: listingDetails,
                triggerObject: 'monthly_rent_and_security_deposit',
                triggerLocation: !listingDetails?.modularListingProgress.step?.includes('review')
                  ? 'posting_path'
                  : 'posting_path_review',
              });
            }}
            data-eventid={EVENT_IDS.LEARN_MORE}
          >
            Learn more
          </Anchor>{' '}
          about fee transparency regulations in your area.
        </Alert>
      )}
    </>
  );
};

export default MonthlyRentFormField;
